import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { DEFAULT_THEME_ID, TABLE_THEMES, TableThemeId } from 'constants/tableThemes';

const STORAGE_KEY = 'hijack_tableSettings';

/**
 * Persisted-blob schema version.
 *
 * v1 (unversioned) — `muteOtherPlayers` meant "hide other players' CHAT" and
 *   defaulted OFF. Because the provider rewrites the whole blob on mount, every
 *   v1 install has `muteOtherPlayers: false` persisted whether or not the player
 *   ever touched the toggle, so a stored `false` cannot be read as a choice.
 * v2 — `muteOtherPlayers` means "mute other players' SOUNDS" and defaults ON.
 * v3 — same meaning, defaults OFF.
 *
 * On reading a v1 blob we therefore drop the old value and apply the current
 * default.
 *
 * WHY v3 FLIPPED IT. The ON default was a blunt answer to a real problem: with
 * every table audible at once, four tables were chaos, and muting everyone else
 * was the only lever available. The sound design now answers that specifically —
 * only the focused table makes ambient noise (EN-10577), the cues that must
 * cross tables are the ones about your own turn, and opponents' actions play at
 * a fraction of your own volume. With the chaos addressed at its source, the
 * blunt lever costs more than it saves: it starts every new player on the
 * quietest possible table and asks them to go into settings to hear the game.
 * Unity defaulted this OFF, so this is also what players arriving from it
 * expect. Turning it ON is still one toggle away for anyone who wants it.
 *
 * The v2->v3 migration can be kinder than v1->v2 was. `muteOtherPlayersSetByUser`
 * exists now, so a v2 blob where the player actually chose the mute keeps that
 * choice; only the ones carrying the persisted DEFAULT (which the provider
 * writes on mount, so nearly all of them) are re-defaulted. The storage key and every other field are untouched, so nothing
 * else about a saved blob is lost. See the comment on `muteOtherPlayers` in
 * `readSettings` for the trade-off this makes.
 *
 * v4 — `tableViewScale` re-defaulted from 1.0 to 1.2 and its range widened.
 *   Same reasoning as v1: the provider rewrites the whole blob on mount, so
 *   EVERY v3 install has `tableViewScale: 1.0` persisted whether or not the
 *   player ever moved the slider. Raising the default constant alone would
 *   therefore reach nobody but brand-new installs — the single most likely way
 *   this change ships and appears to do nothing. `tableViewScaleSetByUser` is
 *   the same device `muteOtherPlayersSetByUser` uses: a v3 blob where the
 *   player actually moved the slider keeps their value (re-clamped to the new
 *   range); one carrying the persisted default is re-defaulted.
 */
export const SETTINGS_SCHEMA_VERSION = 4;

/**
 * The schema version in which `muteOtherPlayers` reached its current meaning and
 * default.
 *
 * Migrations are gated per FIELD, not on "is this the newest schema". Gating the
 * mute migration on the latter coupled it to every future bump: raising the
 * version for the unrelated v4 `tableViewScale` re-default silently re-defaulted
 * mute as well, for every v3 install that had chosen it before
 * `muteOtherPlayersSetByUser` existed.
 */
const MUTE_SETTLED_IN_VERSION = 3;

/**
 * "Table View Size" — the player's card/felt size multiplier.
 *
 * This is no longer a text-and-board-cards control. It multiplies the SEAT's
 * painted scale (see Table/index.tsx `--seat-scale`), so it reaches hole cards,
 * opponent cards, the board, seat name/stack and the dealer marker alike — which
 * is what the menu has always claimed it does.
 *
 * THE OLD CEILING IS GONE, AND WHY THAT IS NOW SAFE. This used to read:
 *
 *   "The MAX is deliberately conservative (1.2 = +20%) so even the 'Largest'
 *    setting can't cause element overlap"
 *
 * That ceiling was the layout's only defence against occlusion, and it was a bad
 * one: it bought safety by keeping every player's cards small, and it still did
 * not work (CX reproduced a blocked flop card at MAXIMUM size, 2026-09-10).
 *
 * It is safe to lift because the scale now feeds the placement maths as well as
 * the painting. `useEllipticalPositions` takes the same combined scale, so bet
 * clearance, dealer clearance and the seat-edge viewport clamp all grow with the
 * furniture instead of being sized for furniture that is no longer that size.
 * Scaling up now moves things APART as well as making them bigger.
 *
 * Range: Small 0.8 -> Largest 1.8, so Largest is 2.25x Small in painted card
 * width (the AC asks for >= 1.5x). Default 1.2 is +20% on the old default of
 * 1.0, at the same viewport and table count.
 */
export const TABLE_VIEW_SCALE_MIN = 0.8;
export const TABLE_VIEW_SCALE_MAX = 1.8;
export const TABLE_VIEW_SCALE_STEP = 0.1;
export const TABLE_VIEW_SCALE_DEFAULT = 1.2;

const clampViewScale = (v: number): number => {
  if (!Number.isFinite(v)) return TABLE_VIEW_SCALE_DEFAULT;
  return Math.min(TABLE_VIEW_SCALE_MAX, Math.max(TABLE_VIEW_SCALE_MIN, v));
};

// Wrap a CSS size so it scales with the player's "Table View Size" (the
// `--table-view-scale` var is set on the table root; defaults to 1 elsewhere).
export const withViewScale = (base: string): string =>
  `calc((${base}) * var(--table-view-scale, 1))`;

type StoredSettings = {
  // Schema version of the persisted blob (see SETTINGS_SCHEMA_VERSION).
  version: number;
  theme: TableThemeId;
  chatEnabled: boolean;
  // "Mute Other Players' Sounds" — AUDIO ONLY. Silences SFX triggered by other
  // seats (bets, calls, checks, folds, all-ins, chip movement); the player still
  // hears their own. It has nothing to do with chat — chat visibility is
  // `chatEnabled` and only `chatEnabled`.
  muteOtherPlayers: boolean;
  // Whether the player has ever TOUCHED the toggle above, as opposed to still
  // sitting on the shipped default.
  //
  // Needed because the provider persists the whole blob on mount, so a stored
  // `muteOtherPlayers: true` is otherwise indistinguishable from the default
  // being written back. Guests and observers hold no seat, so "mute everyone
  // except me" would silence the table entirely for them — we only do that when
  // they actually asked for it. See `shouldPlayTableSfx`.
  muteOtherPlayersSetByUser: boolean;
  // When true, the open-table previews collapse into the "N TABLES" switcher
  // badge; when false, every open table shows as its own fan across the header.
  stackTables: boolean;
  // Player-chosen table-view size multiplier (see constants above).
  tableViewScale: number;
  // Whether the player has ever MOVED the Table View Size slider, as opposed to
  // still sitting on the shipped default. Same purpose as
  // `muteOtherPlayersSetByUser`: the provider persists the whole blob on mount,
  // so a stored scale is otherwise indistinguishable from the default being
  // written back — and the v4 re-default has to tell them apart.
  tableViewScaleSetByUser: boolean;
};

type TableSettingsContextType = StoredSettings & {
  // Per-table theme overrides, keyed by gameID. Session-only (in memory, NOT
  // persisted): a table-specific theme lasts for the table session; a page
  // reload / new session falls back to the global "All My Tables" theme.
  themeByGameId: Record<string, TableThemeId>;
  // Sets the GLOBAL ("All My Tables") theme and clears every per-table override,
  // so all active tables — and any future ones — snap to this theme.
  setTheme: (id: TableThemeId) => void;
  // Sets a theme for a SINGLE table only (its gameID); the global theme and
  // other tables' themes are unaffected.
  setTableTheme: (gameId: string, id: TableThemeId) => void;
  // Resolves the theme a given table should render: its per-table override if
  // set, else the global theme. Call with no gameID to get the global theme.
  getThemeForGame: (gameId?: string) => TableThemeId;
  setChatEnabled: (v: boolean) => void;
  setMuteOtherPlayers: (v: boolean) => void;
  setStackTables: (v: boolean) => void;
  setTableViewScale: (v: number) => void;
  resetTableViewScale: () => void;
};

const defaultSettings: StoredSettings = {
  version: SETTINGS_SCHEMA_VERSION,
  theme: DEFAULT_THEME_ID,
  chatEnabled: true,
  // ON by default — the deliberate Skullcrusher divergence from Unity (which
  // defaults this OFF): a new player starts out hearing only their own SFX.
  // Keep this in step with the readSettings fallback below.
  muteOtherPlayers: false,
  // Nobody has chosen anything yet on a fresh install.
  muteOtherPlayersSetByUser: false,
  // OFF by default: a new player sees every open table as its own fan across the
  // header rather than collapsed behind the "N TABLES" switcher badge, so the
  // tables they have open are visible without opening anything. Turning it ON
  // collapses them. Keep this in step with the readSettings fallback below.
  stackTables: false,
  tableViewScale: TABLE_VIEW_SCALE_DEFAULT,
  // Nobody has moved the slider yet on a fresh install.
  tableViewScaleSetByUser: false
};

const TableSettingsContext = createContext<TableSettingsContextType>({
  ...defaultSettings,
  themeByGameId: {},
  setTheme: () => {},
  setTableTheme: () => {},
  getThemeForGame: () => defaultSettings.theme,
  setChatEnabled: () => {},
  setMuteOtherPlayers: () => {},
  setStackTables: () => {},
  setTableViewScale: () => {},
  resetTableViewScale: () => {}
});

const readSettings = (): StoredSettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSettings;
    const parsed = JSON.parse(raw);
    // A v1 blob's `muteOtherPlayers` recorded a CHAT preference, and the
    // provider persisted it on every mount, so a stored `false` there is the
    // default being written back rather than a choice the player made. We
    // cannot tell those apart, so v1 values are dropped and everyone lands on
    // the v2 default (ON), which is what the ticket asks for untouched installs.
    // From v2 on, a stored boolean IS a choice and is honoured either way.
    const storedVersion = Number(parsed.version);
    // A v2 blob's mute value is authoritative ONLY when the player set it. The
    // provider persists the whole blob on mount, so an untouched v2 install has
    // `muteOtherPlayers: true` stored exactly like a deliberate one — the flag
    // is the only thing that tells them apart, and it is why it exists.
    const choseMuteInV2 = storedVersion >= 2 && parsed.muteOtherPlayersSetByUser === true;
    // GATED ON THE VERSION THAT SETTLED THIS FIELD, not on "is the newest
    // schema". It used to read `storedVersion >= SETTINGS_SCHEMA_VERSION`, which
    // silently coupled every field's migration to every other field's: bumping
    // the version for the v4 `tableViewScale` re-default made every v3 blob
    // "not current" and re-defaulted its unrelated MUTE setting too, turning off
    // the sound preference of everyone who had chosen it before the flag
    // existed. Caught by "honours an explicit v3 ON".
    //
    // v3 is where this field's current meaning and default were established, so
    // any blob at v3 or later carries an authoritative value. Future bumps for
    // unrelated fields now leave it alone.
    const honoursStoredMute = storedVersion >= MUTE_SETTLED_IN_VERSION || choseMuteInV2;
    // `tableViewScaleSetByUser` did not exist before v4, so no pre-v4 blob can
    // prove the slider was moved — every one of them carries the persisted
    // default and is re-defaulted. From v4 on the flag is authoritative.
    const choseTableViewScale = parsed.tableViewScaleSetByUser === true;
    return {
      version: SETTINGS_SCHEMA_VERSION,
      theme: parsed.theme && parsed.theme in TABLE_THEMES ? parsed.theme : DEFAULT_THEME_ID,
      chatEnabled: typeof parsed.chatEnabled === 'boolean' ? parsed.chatEnabled : true,
      muteOtherPlayers:
        honoursStoredMute && typeof parsed.muteOtherPlayers === 'boolean'
          ? parsed.muteOtherPlayers
          : defaultSettings.muteOtherPlayers,
      // Only a v2+ blob can carry a real choice; a v1 blob's value was written
      // by the provider, not by the player, so it counts as untouched.
      muteOtherPlayersSetByUser:
        storedVersion >= 2 && typeof parsed.muteOtherPlayersSetByUser === 'boolean'
          ? parsed.muteOtherPlayersSetByUser
          : false,
      // Same default as above. A stored blob written before this setting existed
      // has no `stackTables` key, so those players also land on OFF.
      stackTables: typeof parsed.stackTables === 'boolean' ? parsed.stackTables : false,
      // v4 re-default. A stored scale is only a CHOICE if the player moved the
      // slider; otherwise it is the mount-time write-back of an older default
      // and must not pin them to the old, too-small size. Honouring it blindly
      // is how this change would have shipped and reached nobody.
      //
      // A deliberate value is kept and re-clamped, so someone who chose the old
      // Smallest (0.8) stays there — the range only widened upward.
      tableViewScale:
        choseTableViewScale && typeof parsed.tableViewScale === 'number'
          ? clampViewScale(parsed.tableViewScale)
          : TABLE_VIEW_SCALE_DEFAULT,
      tableViewScaleSetByUser: choseTableViewScale
    };
  } catch {
    return defaultSettings;
  }
};

const writeSettings = (settings: StoredSettings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* localStorage may be unavailable (private mode, etc.) — settings stay in memory */
  }
};

export const TableSettingsProvider = ({ children }: PropsWithChildren) => {
  const [settings, setSettings] = useState<StoredSettings>(() => readSettings());
  // Per-table theme overrides — session-only, deliberately NOT persisted.
  const [themeByGameId, setThemeByGameId] = useState<Record<string, TableThemeId>>({});

  useEffect(() => {
    writeSettings(settings);
  }, [settings]);

  const setTheme = useCallback((id: TableThemeId) => {
    // Global ("All My Tables"): set the default AND drop every per-table
    // override so all active tables adopt the new global theme.
    setSettings((prev) => ({ ...prev, theme: id }));
    setThemeByGameId({});
  }, []);

  const setTableTheme = useCallback((gameId: string, id: TableThemeId) => {
    if (!gameId) return;
    setThemeByGameId((prev) => ({ ...prev, [gameId]: id }));
  }, []);

  const getThemeForGame = useCallback(
    (gameId?: string): TableThemeId => (gameId && themeByGameId[gameId]) || settings.theme,
    [themeByGameId, settings.theme]
  );

  const setChatEnabled = useCallback((v: boolean) => {
    setSettings((prev) => ({ ...prev, chatEnabled: v }));
  }, []);

  const setMuteOtherPlayers = useCallback((v: boolean) => {
    // Record that this is now a deliberate choice, in BOTH directions — that is
    // what lets an observer opt into a fully silent table (and opt back out).
    setSettings((prev) => ({ ...prev, muteOtherPlayers: v, muteOtherPlayersSetByUser: true }));
  }, []);

  const setStackTables = useCallback((v: boolean) => {
    setSettings((prev) => ({ ...prev, stackTables: v }));
  }, []);

  const setTableViewScale = useCallback((v: number) => {
    // Moving the slider is what makes the value a choice — and what protects it
    // from the next re-default.
    setSettings((prev) => ({
      ...prev,
      tableViewScale: clampViewScale(v),
      tableViewScaleSetByUser: true
    }));
  }, []);

  // Back to the shipped default, and back to being "untouched" — so a player who
  // resets is not pinned to today's default by a future migration either. This
  // is the AC about players who land on Smallest and cannot find their way back.
  const resetTableViewScale = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      tableViewScale: TABLE_VIEW_SCALE_DEFAULT,
      tableViewScaleSetByUser: false
    }));
  }, []);

  // PERF: memoized so consumers only re-render when one of these values
  // actually changes, not on every provider re-render. See
  // docs/investigations/20260819-game-client-overheating.md.
  const value = useMemo(
    () => ({
      ...settings,
      themeByGameId,
      setTheme,
      setTableTheme,
      getThemeForGame,
      setChatEnabled,
      setMuteOtherPlayers,
      setStackTables,
      setTableViewScale,
      resetTableViewScale
    }),
    [
      settings,
      themeByGameId,
      setTheme,
      setTableTheme,
      getThemeForGame,
      setChatEnabled,
      setMuteOtherPlayers,
      setStackTables,
      setTableViewScale,
      resetTableViewScale
    ]
  );

  return <TableSettingsContext.Provider value={value}>{children}</TableSettingsContext.Provider>;
};

export default TableSettingsContext;
